import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getSubcontractedMissions } from '../../services/missionService';
import {
    Card,
    Table,
    Badge,
    Button,
    Spinner,
    Alert,
    Row,
    Col
} from 'react-bootstrap';
import {
    FaRoute,
    FaHandshake,
    FaFileInvoiceDollar,
    FaInfoCircle,
    FaChevronRight,
    FaHistory
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import { useI18n } from '../../i18n/I18nContext';

const STATUS_VARIANTS = (t) => ({
    EN_COURS: { badge: 'primary', text: t('missions.status.en_cours') },
    EN_ATTENTE: { badge: 'warning', text: t('missions.status.en_attente') },
    TERMINEE: { badge: 'success', text: t('missions.status.terminee') },
    ANNULEE: { badge: 'danger', text: t('missions.status.annulee') }
});

const SubcontractedMissions = () => {
    const [subcontracts, setSubcontracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { t } = useI18n();

    useEffect(() => {
        fetchSubcontracts();
    }, []);

    const fetchSubcontracts = async () => {
        try {
            setLoading(true);
            const data = await getSubcontractedMissions();
            setSubcontracts(data || []);
        } catch (err) {
            setError(err.message);
            toast.error(t('subcontracting.toast.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString(t('common.locale') || (t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')); // Using a trick if locale not directly available
    };

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR', {
            style: 'currency',
            currency: currency
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold mb-1">{t('subcontracting.missionsPages.list.title')}</h2>
                        <p className="text-muted mb-0">{t('subcontracting.missionsPages.list.subtitle')}</p>
                    </div>
                    <Button variant="outline-primary" className="rounded-pill px-4" onClick={fetchSubcontracts}>
                        <FaHistory className="me-2" /> {t('common.refresh')}
                    </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {subcontracts.length === 0 ? (
                    <Card className="border-0 shadow-sm rounded-4 p-5 text-center">
                        <div className="mb-4 text-muted">
                            <FaHandshake size={64} className="opacity-25" />
                        </div>
                        <h4>{t('subcontracting.missionsPages.list.empty.title')}</h4>
                        <p className="text-muted">{t('subcontracting.missionsPages.list.empty.subtitle')}</p>
                        <div className="mt-3">
                            <Button variant="primary" className="rounded-pill px-4" onClick={() => navigate('/missions')}>
                                {t('subcontracting.missionsPages.list.empty.action')}
                            </Button>
                        </div>
                    </Card>
                ) : (
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="px-4 py-3 text-uppercase small fw-bold">{t('common.missions')}</th>
                                        <th className="py-3 text-uppercase small fw-bold">{t('subcontracting.missionsPages.detail.sections.partner')}</th>
                                        <th className="py-3 text-uppercase small fw-bold">{t('dashboard.recentMissions.driver')}</th>
                                        <th className="py-3 text-uppercase small fw-bold">{t('common.status.status')}</th>
                                        <th className="py-3 text-uppercase small fw-bold">{t('subcontracting.missionsPages.detail.sections.price')}</th>
                                        <th className="px-4 py-3 text-end"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subcontracts.map((sub) => {
                                        const status = STATUS_VARIANTS(t)[sub.mission.status] || { badge: 'secondary', text: sub.mission.status };
                                        return (
                                            <tr key={sub.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/subcontracted-missions/${sub.mission.id}`)}>
                                                <td className="px-4 py-4">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-3">
                                                            <FaRoute />
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold">{sub.mission.departureLocation} → {sub.mission.arrivalLocation}</div>
                                                            <div className="small text-muted">{t('common.ref')}: {sub.mission.referenceNumber || sub.mission.id.substring(0, 8)}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <FaHandshake className="text-muted me-2" />
                                                        <span className="fw-semibold">{sub.subcontractor.enterpriseName}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="small">
                                                        <div className="fw-bold text-primary">{sub.mission.assignedChauffeurName || t('common.unassigned')}</div>
                                                        <div className="text-muted">{new Date(sub.mission.departureDate).toLocaleDateString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR')}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={status.badge} className="px-3 py-2 rounded-pill fw-normal">
                                                        {status.text}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center text-success fw-bold">
                                                        <FaFileInvoiceDollar className="me-2" />
                                                        {formatCurrency(sub.subcontractingCost, sub.currency)}
                                                    </div>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <Button variant="link" className="text-muted p-0">
                                                        <FaChevronRight />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </Card>
                )}
            </div>
        </DashboardLayout>
    );
};

export default SubcontractedMissions;
